// format.ts — the presentation boundary. Integer minor units in, a string out.
//
// This module does not do arithmetic. It delegates every digit to
// `formatSpark` in src/lib/money.ts, which owns the wire convention (int64
// minor units at precision 4, 1 unit = 1 cent), truncation-not-rounding, the
// real minus sign and locale grouping.
//
// Delegating rather than reimplementing is what makes GOLD's parity a
// STRUCTURAL guarantee instead of a coincidence that drifts: the legacy
// single-currency figure and the GOLD figure are produced by the same function
// with the same dp, so they cannot disagree. There is a regression test that
// says so, but the code is the reason it passes.

import { formatSpark, type FormatSparkOptions } from "@/lib/money";
import { resolveCurrencyPresentation } from "./resolve";
import type { CurrencyCode, CurrencyPresentation, CurrencyPresentationSettings } from "./types";

export interface FormatCurrencyOptions extends Omit<FormatSparkOptions, "dp" | "unitMinor"> {
  /**
   * Override the currency's configured decimals. For prices quoted on a
   * market's tick grid, pass `priceDpForTick(tickSize)` — the grid's precision
   * is a property of the market, not of the currency.
   */
  dp?: number;
  /** Operator configuration. Ignored when `currency` is already resolved. */
  settings?: CurrencyPresentationSettings;
}

/** Accept a bare code or an already-resolved presentation, so a component that
 *  resolved once per render doesn't pay for it again per figure. */
export type CurrencyRef = CurrencyCode | CurrencyPresentation;

export function resolveRef(ref: CurrencyRef, settings?: CurrencyPresentationSettings): CurrencyPresentation {
  return typeof ref === "string" ? resolveCurrencyPresentation(ref, settings) : ref;
}

/**
 * Format int64 minor units as a grouped number string for `currency` — digits
 * only, no symbol and no name.
 *
 *   formatCurrency("GOLD", "737500")  → "7,375.00"
 *   formatCurrency("RED", "-5000")    → "−50.00"
 *
 * Throws UnknownCurrencyError when `currency` is a code outside the fixed set.
 */
export function formatCurrency(
  currency: CurrencyRef,
  minor: string | number | undefined | null,
  opts: FormatCurrencyOptions = {},
): string {
  const { dp, settings, ...rest } = opts;
  const presentation = resolveRef(currency, settings);
  // unitMinor is the currency's, never the caller's: it says what one unit of
  // THIS currency is, which is a property of the currency and not of the call
  // site (SM-484). dp stays overridable because a price's precision is a
  // property of the market's tick grid.
  //
  // The ORIGINAL wire integer goes through — no pre-division. Rescaling here
  // (`minor / (unitMinor / 100)`) would hand formatSpark a float, and its
  // integer path starts with String(Math.trunc(...)), so every sub-cent digit
  // would be dropped at dp > 2: wire 5099 at dp 4 would render 0.5000 instead
  // of 0.5099.
  return formatSpark(minor, {
    ...rest,
    dp: dp ?? presentation.decimals,
    unitMinor: presentation.unitMinor,
  });
}

/**
 * Plain-text amount carrying its unit as a WORD: "7,375.00 Gold".
 *
 * This is the accessible form and the one to use in toasts, aria-labels and
 * anywhere the mark is a picture the reader cannot see. It is also what makes
 * the currency identifiable without colour — a requirement, not a nicety,
 * since RED and GREEN differ chiefly by hue and a colour-blind or
 * monochrome reader must still be able to tell a red balance from a green one.
 */
export function formatCurrencyText(
  currency: CurrencyRef,
  minor: string | number | undefined | null,
  opts: FormatCurrencyOptions = {},
): string {
  const presentation = resolveRef(currency, opts.settings);
  const body = formatCurrency(presentation, minor, opts);
  const placeholder = opts.placeholder ?? "—";
  return body === placeholder ? body : `${body} ${presentation.displayName}`;
}
